import { Store } from '../../core/createStore';
import Order from '../../entity/order';
import OrderRepository from '../../service/orderRepository';
import ValidateRepository from '../../service/validateRepository';
import {
  dispose,
  startFetch,
  finishFetch,
  setOrder,
  setValidShipArea,
} from "../../state/payment";
import * as moment from 'moment';
import Result from "../../entity/result";
import User from "../../entity/user";

class Payment {
  private orderRepository: OrderRepository;
  private validateRepository: ValidateRepository;
  private store: Store;

  constructor({
    orderRepository,
    validateRepository,
    store,
  }: {
    orderRepository: OrderRepository;
    validateRepository: ValidateRepository;
    store: Store;
  }) {
    this.orderRepository = orderRepository;
    this.validateRepository = validateRepository;
    this.store = store;
  }

  public async initialize(): Promise<void> {
    const session = this.store.getState().currentSession;
    const currentStore = this.store.getState().currentStore;
    const user = this.store.getState().currentUser;

    try {
      if (!session || !currentStore) {
        throw new Error('It is cannot have reach here.');
      }

      let shipAreaResult = new Result({});
      let minPriceResult = new Result({});
      this.store.dispatch(startFetch());
      const res = await this.orderRepository.getOrderWithProducts(session);
      let order = res.order.shipDate ? res.order : new Order({ ...res.order, shipDate: moment(new Date()) });
      order.setMinOrderDate(currentStore);

      if (order.region && order.city) {
        [shipAreaResult, minPriceResult] = await Promise.all([
          await this.validShipArea(order),
          await this.validMinPrice(order),
        ]);
      }

      if (user) {
        order = await this.mergeSignedInUser(order, user);
      }

      this.store.dispatch(finishFetch({
        order,
        validShipArea: shipAreaResult,
        validMinPrice: minPriceResult,
      }));
    } catch (e) {
      throw e;
    }
  }

  public async updateEditOrderField(partial: Partial<Order>): Promise<void> {
    const { order: current } = this.store.getState().payment;
    let order = new Order({ ...current, ...partial } as Order);

    if (partial.region) {
      order = new Order({ ...order, city: '', shipAddress1: '', shipAddress2: '' });
    }

    if (order.region && order.city && (partial.city || partial.region)) {
      const [shipAreaResult, minPriceResult] = await Promise.all([
        await this.validShipArea(order),
        await this.validMinPrice(order),
      ]);
      this.store.dispatch(setValidShipArea({
        validShipArea: shipAreaResult,
        validMinPrice: minPriceResult,
      }));

      if (!minPriceResult.result && minPriceResult.validValue) {
        order = new Order({ ...order, minPrice: minPriceResult.validValue });
      }
    }

    this.store.dispatch(setOrder(order));
  }

  public async validShipArea(order: Order): Promise<Result> {
    const session = this.store.getState().currentSession;

    if (!session) {
      throw new Error('It is cannot have reach here.');
    }

    return await this.validateRepository.validShipAreaByOrder(session, order);
  }

  public async validMinPrice(order: Order): Promise<Result> {
    const session = this.store.getState().currentSession;

    if (!session) {
      throw new Error('It is cannot have reach here.');
    }

    return await this.validateRepository.validMinPriceByOrder(session, order);
  }

  public async mergeSignedInUser(order: Order, user: User) {
    return new Order({ ...order, ...Order.fromSingedInUserParams(user) });
  }

  public async save() {
    const session = this.store.getState().currentSession;
    const currentStore = this.store.getState().currentStore;
    const { order: current } = this.store.getState().payment;

    if (!session || !current || !currentStore) {
      throw new Error('It is cannot have reach here.');
    }

    try {
      const newOrder = new Order({ ...current } as Order);

      await this.orderRepository.saveOrder(session, {
        order: newOrder as Order,
      });
    } catch (err) {
      throw err;
    }
  }

  public async saveShipMethod(shipMethod: string) {
    const session = this.store.getState().currentSession;
    const currentStore = this.store.getState().currentStore;
    const { order: current } = this.store.getState().payment;

    if (!session || !current || !currentStore) {
      throw new Error('It is cannot have reach here.');
    }

    try {
      const newOrder = new Order({ ...current, shipMethod } as Order);

      await this.orderRepository.saveOrder(session, {
        order: newOrder as Order,
      });
    } catch (err) {
      throw err;
    }
  }

  public dispose(): void {
    this.store.dispatch(dispose());
  }
}

export default Payment;
